// src/api/Favourite.js
import { http, resolveAssetUrl } from "../lib/http";
import { normalizeWordPressUserID } from "../Utils/UserIdentity";

/* ------------------------------ helpers ------------------------------ */

function normalizeLang(lang) {
  const s = String(lang || "").toUpperCase().trim();
  if (s === "EN" || s === "JP" || s === "ZH") return s;
  return "ZH";
}

function withLangParams(params = {}, lang) {
  return { ...(params || {}), lang: normalizeLang(lang) };
}

/**
 * IMPORTANT:
 * Some servers/plugins may decode %2F in ANY path segment.
 * Keep the SAME behavior as Inquiry.js:
 * - double-encode "%2F" => "%252F"
 */
function encodePathParam(val) {
  const s = encodeURIComponent(String(val ?? ""));
  return s.replace(/%2F/gi, "%252F");
}
function encodePathSeg(val) {
  return encodePathParam(val);
}

function getImagesFromFavouriteRow(row) {
  const candidates = [row?.images, row?.image, row?.url];
  const out = [];

  if (Array.isArray(candidates[0])) {
    for (const x of candidates[0]) {
      if (!x) continue;
      if (typeof x === "string") out.push(x);
      else if (typeof x === "object") {
        const v = x.image || x.url || x.path || x.src;
        if (v) out.push(v);
      }
    }
  } else {
    const v1 = String(candidates[1] || "").trim();
    const v2 = String(candidates[2] || "").trim();
    if (v1) out.push(v1);
    if (v2) out.push(v2);
  }

  const seen = new Set();
  return out
    .map((s) => String(s || "").trim())
    .filter((s) => {
      if (!s || seen.has(s)) return false;
      seen.add(s);
      return true;
    })
    .map(resolveAssetUrl)
    .filter(Boolean);
}

function safeTrim(v) {
  return String(v ?? "").trim();
}

/**
 * Normalize one favourite row into a UI-friendly shape.
 * ✅ IMPORTANT: keep brandID/brandName if backend provides them.
 */
function normalizeFavouriteRow(row) {
  if (!row) return null;

  const images = getImagesFromFavouriteRow(row);
  const image = images[0] || "";

  const variants =
    row?.variants && typeof row.variants === "object" ? row.variants : {};
  const spec = safeTrim(variants["規格"] ?? variants.spec ?? "");
  const weightCapacity = safeTrim(
    variants["重量/容量"] ?? variants.weightCapacity ?? ""
  );
  const dimension = safeTrim(variants["尺寸"] ?? variants.dimension ?? "");

  // ✅ brand passthrough (top-level first, then optional nested fallbacks)
  const brandID =
    safeTrim(row?.brandID) ||
    safeTrim(row?.brandId) ||
    safeTrim(row?.brand_id) ||
    "";

  const brandName =
    safeTrim(row?.brandName) ||
    safeTrim(row?.brand_name) ||
    safeTrim(row?.brand) ||
    safeTrim(row?.Brand) ||
    "";

  return {
    parentID: row?.parentID ?? null,
    productID: safeTrim(row?.productID),
    name: safeTrim(row?.name),
    variants: {
      規格: spec,
      "重量/容量": weightCapacity,
      尺寸: dimension,
    },
    images,
    image,

    // ✅ keep brand fields for FavouritePage filters
    brandID: brandID || null,
    brandName: brandName || null,

    // favourites GET is always favourite
    favourite: 1,
  };
}

function normalizeFavouriteList(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map(normalizeFavouriteRow).filter(Boolean);
}

/* -------------------------------- API -------------------------------- */

export const FavouriteAPI = {
  /**
   * ✅ GET /wp-json/api/favourite/{userID}?lang=...
   */
  async list(userID, { lang } = {}) {
    const uid = normalizeWordPressUserID(userID);
    if (!uid) return [];

    const res = await http.get(`/favourite/${encodePathSeg(uid)}`, {
      params: withLangParams({}, lang),
    });

    const data = res?.data ?? res;
    const rows = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.favourites)
      ? data.favourites
      : [];
    return normalizeFavouriteList(rows);
  },

  /**
   * ✅ NEW: GET /wp-json/api/favourite/{userID}/count
   * Returns a JSON number, e.g. 5
   */
  async count(userID) {
    const uid = normalizeWordPressUserID(userID);
    if (!uid) return 0;

    const res = await http.get(`/favourite/${encodePathSeg(uid)}/count`);
    const data = res?.data ?? res;

    // backend returns number only, but be defensive
    const n =
      typeof data === "number"
        ? data
        : typeof data === "string"
        ? Number(data)
        : Number(data?.count ?? data?.data ?? 0);

    return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
  },

  /**
   * ✅ POST /wp-json/api/favourite/add
   * Body: { userID, productID }
   */
  async add(userID, productID) {
    const uid = normalizeWordPressUserID(userID);
    const pid = String(productID || "").trim();
    if (!uid || !pid) {
      const err = new Error("userID and productID are required.");
      err.response = { data: { error: "userID and productID are required." } };
      throw err;
    }

    const res = await http.post(`/favourite/add`, {
      userID: uid,
      productID: pid,
    });

    return res?.data ?? res;
  },

  /**
   * ✅ POST /wp-json/api/favourite/remove
   * Body: { userID, productID }
   */
  async remove(userID, productID) {
    const uid = normalizeWordPressUserID(userID);
    const pid = String(productID || "").trim();
    if (!uid || !pid) {
      const err = new Error("userID and productID are required.");
      err.response = { data: { error: "userID and productID are required." } };
      throw err;
    }

    const res = await http.post(`/favourite/remove`, {
      userID: uid,
      productID: pid,
    });

    return res?.data ?? res;
  },

  /**
   * Convenience: toggle favourite
   * - If currently favourite => remove
   * - else => add
   */
  async toggle(userID, productID, currentState) {
    if (currentState) return this.remove(userID, productID);
    return this.add(userID, productID);
  },

  /**
   * Convenience: fast check if product is favourited
   * - If you already have list, prefer using a Set in UI.
   * - This will fetch list (slower).
   */
  async isFavourite(userID, productID, { lang } = {}) {
    const pid = String(productID || "").trim();
    if (!pid) return false;
    const list = await this.list(userID, { lang });
    return list.some((x) => String(x.productID || "") === pid);
  },
};

export default FavouriteAPI;
